/**
 * shadcn/ui Button — экспортирует и компонент, и CVA-функцию `buttonVariants`.
 * Это официальный паттерн shadcn; НЕ разносим на два файла — иначе
 * `npx shadcn add button` при обновлении сломает наши правки.
 */
/* eslint-disable react-refresh/only-export-components */
import React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "./utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap font-semibold transition-all duration-150 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-5 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground active:scale-95",
        destructive:
          "bg-destructive text-white focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 active:scale-95",
        outline:
          "border border-primary/20 bg-card text-primary active:scale-95",
        secondary:
          "bg-[var(--primary-purple-light)] text-[var(--primary-purple)] active:scale-95",
        ghost:
          "active:scale-95",
        surface:
          "bg-card text-foreground shadow-[var(--shadow)] active:scale-95",
        link: "text-primary underline-offset-4",
        "icon-white": "bg-white text-gray-900 active:scale-95",
        "icon-white-semi": "bg-white text-gray-900 active:scale-95",
      },
      size: {
        default: "h-12 px-4 py-2 text-base has-[>svg]:px-3 rounded-[var(--radius)]",
        sm: "h-[38px] px-3 text-base has-[>svg]:px-2.5 rounded-[var(--radius)]",
        icon: "size-12 [&_svg:not([class*='size-'])]:size-5 rounded-[var(--radius-full)]",
        "icon-sm": "size-11 [&_svg:not([class*='size-'])]:size-4 rounded-[var(--radius-full)]",
        "icon-header": "size-11 [&_svg:not([class*='size-'])]:size-[18px] rounded-[var(--radius-full)]",
        "icon-lg": "size-12 [&_svg:not([class*='size-'])]:size-6 rounded-[var(--radius-full)]",
        "icon-xl": "size-14 [&_svg:not([class*='size-'])]:size-7 rounded-[var(--radius-full)]",
        "icon-2xl": "size-16 [&_svg:not([class*='size-'])]:size-8 rounded-[var(--radius-full)]",
        "icon-square": "size-9 [&_svg:not([class*='size-'])]:size-4 rounded-[var(--radius-sm)]",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

const Button = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<"button"> &
    VariantProps<typeof buttonVariants> & {
      asChild?: boolean;
    }
>(({
  className,
  variant,
  size,
  asChild = false,
  ...props
}, ref) => {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props}
    />
  );
});

Button.displayName = "Button";

export { Button, buttonVariants };